/**
 * AD-040 BATCH-1 (2026-06-25) — shared reusable COORDINATION ROLE list.
 *
 * The canonical roles live in `designProgramme.ts` (`COORDINATION_ROLES`). When
 * staff pick "Other…" and name a new role, it is saved to a SHARED settings doc
 * `settings/coordination_roles.roles[]` so it's reusable across projects — the
 * same "live custom-add → reusable list" pattern as the AUO palette.
 *
 * Rules: `settings/{document=**}` is read=isTeam / write=isHrRole (firestore.rules
 * line 921) — so a custom role add is permitted with NO rules change.
 *
 * Pure helpers (merge/normalise) are Firebase-free; the listener/writer touch the
 * SDK at the bottom.
 */
import {
  arrayUnion,
  doc,
  onSnapshot,
  setDoc,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from './firebase';
import { COORDINATION_ROLES } from './designProgramme';

const ROLES_DOC = 'coordination_roles';

/** PURE — canonical + custom roles, de-duped (case-insensitive), canonical first. */
export function mergeRoles(custom: string[]): string[] {
  const out: string[] = [...COORDINATION_ROLES];
  const seen = new Set(out.map((r) => r.toLowerCase()));
  for (const c of custom) {
    const v = (c || '').trim();
    if (v && !seen.has(v.toLowerCase())) {
      out.push(v);
      seen.add(v.toLowerCase());
    }
  }
  return out;
}

/** Live listener for the shared custom-role list (returns the merged set). */
export function listenCoordinationRoles(
  cb: (roles: string[]) => void,
  onErr?: (e: Error) => void,
): Unsubscribe {
  return onSnapshot(
    doc(db, 'settings', ROLES_DOC),
    (snap) => {
      const d = snap.data() as { roles?: unknown } | undefined;
      const custom = Array.isArray(d?.roles)
        ? (d!.roles as unknown[]).filter((x): x is string => typeof x === 'string')
        : [];
      cb(mergeRoles(custom));
    },
    (e) => {
      // self-heal: surface the error, fall back to canonical roles only.
      console.error('[coordinationRoles] listener error', e);
      cb(mergeRoles([]));
      onErr?.(e);
    },
  );
}

/** Add a custom role to the shared list (idempotent via arrayUnion). */
export async function addCoordinationRole(role: string): Promise<void> {
  const v = (role || '').trim();
  if (!v) return;
  // Don't re-store a canonical role.
  if ((COORDINATION_ROLES as readonly string[]).some((r) => r.toLowerCase() === v.toLowerCase())) return;
  await setDoc(
    doc(db, 'settings', ROLES_DOC),
    { roles: arrayUnion(v), updatedAt: new Date().toISOString() },
    { merge: true },
  );
}
